<script setup lang="ts">
import { ref } from "vue";
import { Search, MapPin, Loader2 } from "@lucide/vue";
import { Button } from "@/components/ui/button";
import type {
  LocationSearchState,
  LocationSearchResult,
} from "@/stores/locationSearch.store";

defineProps<{
  state: LocationSearchState;
}>();

const emit = defineEmits<{
  query: [value: string];
  select: [location: LocationSearchResult];
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const showResults = ref(false);

/** Forward search input and reveal matching results. */
function updateQuery(event: Event) {
  showResults.value = true;
  emit("query", (event.target as HTMLInputElement).value);
}

/** Hide results when focus leaves the search area. */
function closeResults(event: FocusEvent) {
  if (
    !(event.currentTarget as HTMLElement).contains(
      event.relatedTarget as Node | null,
    )
  )
    showResults.value = false;
}

/** Focus the location search input. */
function focus() {
  inputRef.value?.focus();
}

defineExpose({ focus });
</script>

<template>
  <div class="relative" @focusout="closeResults">
    <!-- Search Input styled like active location card -->
    <div
      class="relative rounded-control border border-app-border bg-app-elevated shadow-none focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent"
    >
      <div class="flex items-center gap-3 px-3 py-2.5">
        <Search class="h-4 w-4 flex-shrink-0 text-text-muted" />
        <input
          ref="inputRef"
          :value="state.query"
          @input="updateQuery"
          type="text"
          placeholder="Search city or ZIP"
          aria-label="Search city or ZIP"
          class="min-w-0 flex-1 border-0 bg-transparent text-sm leading-normal text-text-primary outline-none placeholder:text-text-muted focus:shadow-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          @focus="showResults = state.results.length > 0"
        />
        <Loader2
          v-if="state.loading"
          class="h-4 w-4 flex-shrink-0 animate-spin text-text-muted"
        />
      </div>
    </div>

    <!-- Search Results Dropdown -->
    <div
      v-if="showResults && state.results.length > 0"
      class="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-control border border-app-border bg-app-surface p-1 shadow-popover"
    >
      <Button
        v-for="result in state.results"
        :key="`${result.lat}-${result.lng}`"
        variant="menu"
        radius="small"
        class="w-full justify-start gap-3 border-0 p-3 text-left"
        @click="emit('select', result)"
      >
        <MapPin class="h-4 w-4 flex-shrink-0 text-text-muted" />
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm text-text-primary">{{ result.name }}</p>
          <p class="truncate text-[11px] text-text-muted">
            <span v-if="result.state">{{ result.state }}, </span
            >{{ result.country }}
          </p>
        </div>
      </Button>
    </div>

    <!-- No Results -->
    <div
      v-if="
        showResults &&
        !state.loading &&
        state.query.length >= 2 &&
        state.results.length === 0
      "
      class="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-control border border-app-border bg-app-surface p-1 shadow-popover"
    >
      <p class="text-center text-sm text-text-muted">
        {{ state.error || "No locations found" }}
      </p>
    </div>
  </div>
</template>
